import {
  useCallback,
  forwardRef,
  Ref,
  useState,
  ChangeEvent,
  useEffect,
  ReactElement,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";

import { Amount } from "@evergiving/common/dist/es2015/pledge";
import {
  PaymentFrequencyConfig,
  CustomAmountConfig,
} from "@evergiving/common/dist/es2015/form";

import { RootState } from "@/lib/redux/types";
import {
  setPresetAmount,
  setFormValue,
  resetFormErrors,
} from "@/lib/redux/actions";
import { RadioGroup, RadioOptionSpec } from "@/lib/components/RadioGroup";
import { FormattedNumber } from "@/lib/components/FormattedNumber";
import {
  parseAmount,
  formatAmount,
  currencySymbol,
} from "@/lib/misc/parseAmount";
import { FieldError } from "@/lib/components/FieldError";

export const customAmountStringErrorId = "customAmountStringErrors";

function getCustomAmountStringErrors({
  customAmountConfig,
  paymentConfig,
  locale,
  numericalAmount,
}: {
  customAmountConfig: CustomAmountConfig;
  paymentConfig: Pick<PaymentFrequencyConfig, "presetAmounts" | "customAmount">;
  locale: string;
  numericalAmount: number | undefined;
}): string[] {
  const errors = [];
  const {
    minimum: minimumFromConfig,
    maximum: maximumFromConfig,
  } = customAmountConfig;
  let errorText = null;
  if (numericalAmount === undefined) {
    errorText = customAmountConfig.invalidAmountText;
  } else if (minimumFromConfig && numericalAmount < minimumFromConfig.value) {
    errorText = minimumFromConfig.errorText;
  } else if (
    maximumFromConfig !== undefined &&
    numericalAmount > maximumFromConfig.value
  ) {
    errorText = maximumFromConfig.errorText;
  }

  if (errorText) {
    const { customAmount } = paymentConfig;
    if (customAmount) {
      const { minimum, maximum } = customAmount;
      if (minimum) {
        errorText = errorText.replace(
          "{MINIMUM}",
          formatAmount(locale, minimum.value)
        );
      }
      if (maximum) {
        errorText = errorText.replace(
          "{MAXIMUM}",
          formatAmount(locale, maximum.value)
        );
      }
    }
    errors.push(errorText);
  }

  return errors;
}

const CustomAmount = forwardRef(
  (
    {
      selected,
      locale,
      customAmountConfig,
      paymentConfig,
    }: {
      selected: boolean;
      locale: string;
      customAmountConfig: CustomAmountConfig;
      paymentConfig: Pick<
        PaymentFrequencyConfig,
        "presetAmounts" | "customAmount"
      >;
    },
    ref?: Ref<HTMLElement>
  ): ReactElement => {
    const customAmountString = useSelector(
      (state: RootState) => state.customAmountString.value
    );

    const anyErrors = useSelector(
      (state: RootState) => state.errors.customAmountString
    );

    const [currentAmountString, setCurrentAmountString] = useState<string>(
      customAmountString || ""
    );

    const dispatch = useDispatch();

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      setCurrentAmountString(event.target.value);
    }, []);

    const handleFocus = useCallback(() => {
      dispatch(resetFormErrors("customAmountString"));
    }, [dispatch]);

    const handleBlur = useCallback(() => {
      const trimmed = currentAmountString.trim();
      if (trimmed.length === 0) {
        dispatch(setFormValue("customAmountString", undefined, false));
      } else {
        const numericalAmount = parseAmount(currentAmountString);

        dispatch(
          setFormValue(
            "customAmountString",
            numericalAmount === undefined
              ? trimmed
              : formatAmount(locale, numericalAmount),
            getCustomAmountStringErrors({
              customAmountConfig,
              paymentConfig,
              locale,
              numericalAmount,
            }).length > 0
          )
        );
      }
    }, [
      currentAmountString,
      dispatch,
      locale,
      customAmountConfig,
      paymentConfig,
    ]);

    const handleKeyDown = useCallback(
      (keyEvent: React.KeyboardEvent<HTMLInputElement>) => {
        if (keyEvent.code === "ArrowLeft" || keyEvent.code === "ArrowRight") {
          keyEvent.stopPropagation();
        }
      },
      []
    );

    useEffect(() => {
      setCurrentAmountString(customAmountString || "");
    }, [customAmountString]);

    return (
      <>
        <label
          htmlFor="customAmountInput"
          data-content={customAmountConfig.labelText}
          tabIndex={-1}
        >
          {customAmountConfig.labelText}
        </label>
        <div className="customAmountHighlight">
          <div className="customCurrencyContainer" tabIndex={-1}>
            <div
              aria-label={customAmountConfig.currencyForOtherAmountText}
              className="customCurrency"
            >
              {currencySymbol(locale, customAmountConfig.allowedCurrencies[0])}
            </div>
          </div>
          <input
            type="text"
            id="customAmountInput"
            value={currentAmountString}
            onChange={handleChange}
            autoComplete="off"
            inputMode="decimal"
            tabIndex={selected ? 0 : -1}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={ref as Ref<HTMLInputElement>}
            aria-invalid={anyErrors || undefined}
            aria-describedby={anyErrors ? customAmountStringErrorId : undefined}
          />
        </div>
      </>
    );
  }
);
CustomAmount.displayName = "CustomAmount";

export function AmountOptions({
  locale,
  donationAmountText,
  paymentConfig,
}: {
  locale: string;
  donationAmountText: string;
  paymentConfig: Pick<PaymentFrequencyConfig, "presetAmounts" | "customAmount">;
}): ReactElement {
  const presetAmount = useSelector((state: RootState) => state.presetAmount);
  const dispatch = useDispatch();

  const anyCustomAmountStringErrors = useSelector(
    (state: RootState) => state.errors.customAmountString
  );

  const handleChange = useCallback(
    (newValue: Amount | null) => {
      dispatch(setPresetAmount(newValue, locale));
    },
    [dispatch, locale]
  );

  const options: RadioOptionSpec<Amount | null>[] = paymentConfig.presetAmounts.map(
    (value) => ({
      value,
      content: function AmountOptionContent(): ReactElement {
        return (
          <div>
            <FormattedNumber
              locale={locale}
              value={value.value / 100.0}
              currency={value.currency}
            />
          </div>
        );
      },
      className: "presetAmount",
    })
  );

  const customAmountConfig = paymentConfig.customAmount;
  if (customAmountConfig) {
    options.push({
      value: null,
      content: function CustomAmountOptionContent(
        selected: boolean,
        divRef
      ): ReactElement {
        return (
          <CustomAmount
            ref={divRef}
            selected={selected}
            locale={locale}
            customAmountConfig={customAmountConfig}
            paymentConfig={paymentConfig}
          />
        );
      },
      tabIndex: -1,
      wantsRef: true,
      className: classnames(
        "customAmount",
        `grid-column-end-${paymentConfig.presetAmounts.length + 1}`
      ),
    });
  }

  const customAmountString = useSelector(
    (state: RootState) => state.customAmountString.value
  );

  return (
    <>
      <RadioGroup<Amount | null>
        className="amountSelector"
        value={presetAmount}
        onChange={handleChange}
        ariaLabel={donationAmountText}
        ariaRequired
        autoFocus
      >
        {options}
      </RadioGroup>
      {customAmountConfig && anyCustomAmountStringErrors && (
        <div role="alert" className="customAmountAlert">
          <FieldError
            id={customAmountStringErrorId}
            messages={getCustomAmountStringErrors({
              customAmountConfig,
              paymentConfig,
              locale,
              numericalAmount: customAmountString
                ? parseAmount(customAmountString)
                : undefined,
            })}
          />
        </div>
      )}
    </>
  );
}
